import { db } from '../config';
import { collection, getDocs, query, where, doc, getDoc, updateDoc, increment, Timestamp } from 'firebase/firestore';

// Discount types
export interface Discount {
  id: string;
  type: 'percentage' | 'fixed';
  amount: number;
  isActive: boolean;
  startDate: Date;
  endDate?: Date;
  applicableVesselTypes?: string[];
}

export interface PromoDiscount extends Discount {
  discountType: 'promo';
  code: string;
  usageLimit?: number;
  usageCount: number;
  minPurchaseAmount?: number;
  userRestriction?: 'new' | 'returning' | 'none';
}

export interface TimeDiscount extends Discount {
  discountType: 'time';
  name: string;
  daysOfWeek?: string[];
  hoursOfDay?: number[];
}

export interface UserDiscount extends Discount {
  discountType: 'user';
  name: string;
  userType: 'new' | 'returning' | 'loyal' | 'all';
  minBookings?: number;
  maxUses?: number;
}

/**
 * Validates a promo code for a reservation
 * @param code The promo code to validate
 * @param userId The ID of the user making the reservation
 * @param totalAmount The total amount of the reservation
 * @param vesselType The type of vessel being reserved
 * @param bookingDate The date of the booking
 * @returns The discount amount and discount details if valid, null otherwise
 */
export const validatePromoCode = async (
  code: string, 
  userId: string, 
  totalAmount: number,
  vesselType: string,
  bookingDate: Date
): Promise<{ discount: number; discountType: 'percentage' | 'fixed'; discountDetails: PromoDiscount } | null> => {
  try {
    console.log('validatePromoCode called with:', { code, userId, totalAmount, vesselType, bookingDate });
    
    // Query for the promo code
    const promoQuery = query(
      collection(db, 'discounts'),
      where('discountType', '==', 'promo'),
      where('isActive', '==', true)
    );
    
    console.log('Querying for all active promo codes');
    const snapshot = await getDocs(promoQuery);
    
    console.log('Active promo codes found:', snapshot.size);
    
    // Manually filter to find a case-insensitive match
    const matchingDocs = snapshot.docs.filter(doc => {
      const data = doc.data();
      return data.code && (data.code.toUpperCase() === code.toUpperCase());
    });
    
    console.log('Matching promo codes after case-insensitive filter:', matchingDocs.length);
    
    if (matchingDocs.length === 0) {
      console.log('Promo code not found after case-insensitive check');
      return null; // Promo code not found
    }
    
    // Get the promo code data
    const promoDoc = matchingDocs[0];
    const promoData = promoDoc.data() as Omit<PromoDiscount, 'id' | 'startDate' | 'endDate'> & {
      startDate: Timestamp;
      endDate?: Timestamp;
    };
    
    console.log('Promo code found in database:', promoData);
    
    const promoCode: PromoDiscount = {
      id: promoDoc.id,
      ...promoData,
      startDate: promoData.startDate.toDate(),
      endDate: promoData.endDate ? promoData.endDate.toDate() : undefined
    };
    
    console.log('Converted promo code startDate:', promoCode.startDate);
    console.log('Converted promo code endDate:', promoCode.endDate);
    
    // Validate the promo code
    const now = new Date();
    console.log('Current date for comparison:', now);
    console.log('Current date in ISO format:', now.toISOString());
    
    // Ensure proper date comparison by normalizing to UTC strings and back to dates
    // This helps avoid time zone issues
    const startDateUTC = new Date(promoCode.startDate.toISOString());
    const endDateUTC = promoCode.endDate ? new Date(promoCode.endDate.toISOString()) : undefined;
    const nowUTC = new Date(now.toISOString());
    
    console.log('Normalized dates for comparison:');
    console.log('Start date UTC:', startDateUTC.toISOString());
    console.log('End date UTC:', endDateUTC ? endDateUTC.toISOString() : 'Not set');
    console.log('Current date UTC:', nowUTC.toISOString());
    
    // Check if the promo code is valid for the current date
    const isBeforeStartDate = nowUTC < startDateUTC;
    const isAfterEndDate = endDateUTC && nowUTC > endDateUTC;
    
    console.log('Date validation checks:', { 
      isBeforeStartDate, 
      isAfterEndDate,
      currentDateTimestamp: nowUTC.getTime(),
      startDateTimestamp: startDateUTC.getTime(),
      endDateTimestamp: endDateUTC ? endDateUTC.getTime() : 'N/A'
    });
    
    if (isBeforeStartDate || isAfterEndDate) {
      console.log('Promo code invalid due to date restrictions');
      return null; // Promo code has expired or not yet valid
    }
    
    // Check if the promo code has reached its usage limit
    if (promoCode.usageLimit !== undefined && promoCode.usageLimit !== null && promoCode.usageCount >= promoCode.usageLimit) {
      console.log('Promo code usage limit reached');
      return null; // Promo code has reached its usage limit
    }
    
    // Check if the booking amount meets the minimum purchase amount
    if (promoCode.minPurchaseAmount !== undefined && promoCode.minPurchaseAmount !== null && totalAmount < promoCode.minPurchaseAmount) {
      console.log('Booking amount does not meet minimum purchase requirement');
      return null; // Booking amount does not meet the minimum purchase amount
    }
    
    // Check if the promo code is applicable to the vessel type
    if (promoCode.applicableVesselTypes && promoCode.applicableVesselTypes.length > 0 &&
        !promoCode.applicableVesselTypes.includes(vesselType)) {
      console.log('Promo code not applicable to this vessel type');
      return null; // Promo code is not applicable to the vessel type
    }
    
    // TODO: Check user restriction based on booking history
    // This would require querying the user's booking history
    
    // Calculate the discount amount
    let discountAmount: number;
    if (promoCode.type === 'percentage') {
      discountAmount = (totalAmount * promoCode.amount) / 100;
      console.log(`Calculated percentage discount: ${promoCode.amount}% of $${totalAmount} = $${discountAmount}`);
    } else {
      discountAmount = promoCode.amount;
      console.log(`Using fixed discount amount: $${discountAmount}`);
    }
    
    // Return the discount amount and the discount details
    console.log('Promo code validation successful');
    return {
      discount: discountAmount,
      discountType: promoCode.type,
      discountDetails: promoCode
    };
  } catch (error) {
    console.error('Error validating promo code:', error);
    return null;
  }
};

/**
 * Applies a promo code to a reservation
 * @param promoCodeId The ID of the promo code to apply
 * @returns True if the promo code was successfully applied, false otherwise
 */
export const applyPromoCode = async (promoCodeId: string): Promise<boolean> => {
  try {
    // Increment the usage count of the promo code
    const promoRef = doc(db, 'discounts', promoCodeId);
    await updateDoc(promoRef, {
      usageCount: increment(1)
    });
    
    return true;
  } catch (error) {
    console.error('Error applying promo code:', error);
    return false;
  }
};

/**
 * Gets all applicable time-based discounts for a booking
 * @param bookingDate The date and time of the booking
 * @param vesselType The type of vessel being booked
 * @returns An array of applicable time-based discounts
 */
export const getApplicableTimeDiscounts = async (
  bookingDate: Date,
  vesselType: string
): Promise<TimeDiscount[]> => {
  try {
    // Query for active time-based discounts
    const timeQuery = query(
      collection(db, 'discounts'),
      where('discountType', '==', 'time'),
      where('isActive', '==', true)
    );
    
    const snapshot = await getDocs(timeQuery);
    
    if (snapshot.empty) {
      return [];
    }
    
    const now = new Date();
    const dayOfWeek = bookingDate.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    const hourOfDay = bookingDate.getHours();
    
    // Filter time-based discounts that are applicable to the booking
    const applicableDiscounts: TimeDiscount[] = [];
    
    snapshot.forEach((doc) => {
      const data = doc.data() as Omit<TimeDiscount, 'id' | 'startDate' | 'endDate'> & {
        startDate: Timestamp;
        endDate?: Timestamp;
      };
      
      const timeDiscount: TimeDiscount = {
        id: doc.id,
        ...data,
        startDate: data.startDate.toDate(),
        endDate: data.endDate ? data.endDate.toDate() : undefined
      };
      
      // Check if the discount is valid for the current date
      if (now < timeDiscount.startDate || (timeDiscount.endDate && now > timeDiscount.endDate)) {
        return; // Discount has expired or not yet valid
      }
      
      // Check if the discount is applicable to the vessel type
      if (timeDiscount.applicableVesselTypes && timeDiscount.applicableVesselTypes.length > 0 &&
          !timeDiscount.applicableVesselTypes.includes(vesselType)) {
        return; // Discount is not applicable to the vessel type
      }
      
      // Check if the discount is applicable to the day of the week
      if (timeDiscount.daysOfWeek && timeDiscount.daysOfWeek.length > 0 &&
          !timeDiscount.daysOfWeek.includes(dayOfWeek)) {
        return; // Discount is not applicable to the day of the week
      }
      
      // Check if the discount is applicable to the hour of the day
      if (timeDiscount.hoursOfDay && timeDiscount.hoursOfDay.length > 0 &&
          !timeDiscount.hoursOfDay.includes(hourOfDay)) {
        return; // Discount is not applicable to the hour of the day
      }
      
      // Discount is applicable to the booking
      applicableDiscounts.push(timeDiscount);
    });
    
    return applicableDiscounts;
  } catch (error) {
    console.error('Error getting applicable time discounts:', error);
    return [];
  }
};

/**
 * Gets all applicable user-based discounts for a user
 * @param userId The ID of the user
 * @param bookingCount The number of previous bookings the user has made
 * @param vesselType The type of vessel being booked
 * @returns An array of applicable user-based discounts
 */
export const getApplicableUserDiscounts = async (
  userId: string,
  bookingCount: number,
  vesselType: string
): Promise<UserDiscount[]> => {
  try {
    // Query for active user-based discounts
    const userQuery = query(
      collection(db, 'discounts'),
      where('discountType', '==', 'user'),
      where('isActive', '==', true)
    );
    
    const snapshot = await getDocs(userQuery);
    
    if (snapshot.empty) {
      return [];
    }
    
    const now = new Date();
    
    // Filter user-based discounts that are applicable to the user
    const applicableDiscounts: UserDiscount[] = [];
    
    snapshot.forEach((doc) => {
      const data = doc.data() as Omit<UserDiscount, 'id' | 'startDate' | 'endDate'> & {
        startDate: Timestamp;
        endDate?: Timestamp;
      };
      
      const userDiscount: UserDiscount = {
        id: doc.id,
        ...data,
        startDate: data.startDate.toDate(),
        endDate: data.endDate ? data.endDate.toDate() : undefined
      };
      
      // Check if the discount is valid for the current date
      if (now < userDiscount.startDate || (userDiscount.endDate && now > userDiscount.endDate)) {
        return; // Discount has expired or not yet valid
      }
      
      // Check if the discount is applicable to the vessel type
      if (userDiscount.applicableVesselTypes && userDiscount.applicableVesselTypes.length > 0 &&
          !userDiscount.applicableVesselTypes.includes(vesselType)) {
        return; // Discount is not applicable to the vessel type
      }
      
      // Check if the discount is applicable to the user type
      if (userDiscount.userType === 'new' && bookingCount > 0) {
        return; // Discount is only for new users
      }
      
      if (userDiscount.userType === 'returning' && bookingCount === 0) {
        return; // Discount is only for returning users
      }
      
      if (userDiscount.userType === 'loyal' && 
          userDiscount.minBookings !== undefined && 
          bookingCount < userDiscount.minBookings) {
        return; // Discount is only for loyal users with minimum bookings
      }
      
      // TODO: Check if the user has already used this discount up to maxUses
      // This would require querying the user's discount usage history
      
      // Discount is applicable to the user
      applicableDiscounts.push(userDiscount);
    });
    
    return applicableDiscounts;
  } catch (error) {
    console.error('Error getting applicable user discounts:', error);
    return [];
  }
};

/**
 * Calculates the best discount to apply to a booking
 * @param promoDiscount The promo discount (if any)
 * @param timeDiscounts Array of applicable time-based discounts
 * @param userDiscounts Array of applicable user-based discounts
 * @param totalAmount The total amount of the booking
 * @returns The best discount to apply
 */
export const calculateBestDiscount = (
  promoDiscount: { discount: number; discountType: 'percentage' | 'fixed'; discountDetails: PromoDiscount } | null,
  timeDiscounts: TimeDiscount[],
  userDiscounts: UserDiscount[],
  totalAmount: number
): { 
  discountAmount: number; 
  discountType: 'promo' | 'time' | 'user' | null;
  discountId: string | null;
  discountDescription: string;
} => {
  let bestDiscount = {
    discountAmount: 0,
    discountType: null as 'promo' | 'time' | 'user' | null,
    discountId: null as string | null,
    discountDescription: 'No discount applied'
  };
  
  // Check promo discount
  if (promoDiscount) {
    bestDiscount = {
      discountAmount: promoDiscount.discount,
      discountType: 'promo',
      discountId: promoDiscount.discountDetails.id,
      discountDescription: `Promo code: ${promoDiscount.discountDetails.code} (${promoDiscount.discountDetails.amount}${promoDiscount.discountType === 'percentage' ? '%' : '$'} off)`
    };
  }
  
  // Check time-based discounts
  timeDiscounts.forEach(discount => {
    let discountAmount: number;
    if (discount.type === 'percentage') {
      discountAmount = (totalAmount * discount.amount) / 100;
    } else {
      discountAmount = discount.amount;
    }
    
    if (discountAmount > bestDiscount.discountAmount) {
      bestDiscount = {
        discountAmount,
        discountType: 'time',
        discountId: discount.id,
        discountDescription: `Time discount: ${discount.name} (${discount.amount}${discount.type === 'percentage' ? '%' : '$'} off)`
      };
    }
  });
  
  // Check user-based discounts
  userDiscounts.forEach(discount => {
    let discountAmount: number;
    if (discount.type === 'percentage') {
      discountAmount = (totalAmount * discount.amount) / 100;
    } else {
      discountAmount = discount.amount;
    }
    
    if (discountAmount > bestDiscount.discountAmount) {
      bestDiscount = {
        discountAmount,
        discountType: 'user',
        discountId: discount.id,
        discountDescription: `User discount: ${discount.name} (${discount.amount}${discount.type === 'percentage' ? '%' : '$'} off)`
      };
    }
  });
  
  return bestDiscount;
}; 